import React, {
    FC,
    ReactNode,
    useMemo,
    useState,
    createContext,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import i18n from '../../i18n';

import { useServiceData, useFaqsData, usePrefiltersData } from '../hooks/useAppContextHooks';
import { ParametrosAPI } from '../http-requests';
import { APIHelper } from '../helpers';
import { insertAtividades } from '../../store/ducks/pageConfig';
import { FaqType } from '../../modules/consulta/components/mobile/types/FaqsTypes';
import { PrefilterType } from '../types/PrefilterType';

export type LangType = 'pt' | 'en' | 'es';

interface AppContextProviderProps {
    children: ReactNode;
}

export const langStorageIndex: string = 'i18nextLng';

interface ServiceResponse {
    siglaInstituicao?: string;
    dados?: any;
    showDestaques?: boolean;
}

interface AppContextProps {
    lang: LangType;
    changeLang: (lang: LangType) => void;
    accessByAccessibilityDetails: boolean;
    setAccessByAccessibilityDetails: React.Dispatch<React.SetStateAction<boolean>>;
    onGoBackMobile: (() => void) | null;
    setOnGoBackMobile: React.Dispatch<React.SetStateAction<(() => void) | null>>;
    mobileView: string | null;
    setMobileView: React.Dispatch<React.SetStateAction<string | null>>;
    serviceData: any;
    faqsData: FaqType[] | null;
    sectionsData: any;
    setSectionsData: React.Dispatch<React.SetStateAction<any>>;
    isMobile: boolean;
    selectedTab: any;
    setSelectedTab: React.Dispatch<React.SetStateAction<any>>;
    prefiltersData: PrefilterType[] | null;
    siglaInstituicao?: string;
    showDestaques: boolean;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

const AppContextProvider: FC<AppContextProviderProps> = ({ children }) => {

    const inSessionLang = localStorage.getItem(langStorageIndex) as LangType | null;
    const [lang, setLang] = useState<LangType>(inSessionLang || 'pt');

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const observer = useRef<ResizeObserver | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 767);

    // 🔹 Service agora retorna objeto completo da API
    const serviceResponse: ServiceResponse | null = useServiceData(lang);

    // 🔹 Separação clara
    const siglaInstituicao = serviceResponse?.siglaInstituicao;
    const serviceData = serviceResponse?.dados ?? null;
    const showDestaques = Boolean(
        serviceResponse?.showDestaques
            ?? serviceResponse?.dados?.showDestaques
            ?? serviceResponse?.dados?.show_destaques
            ?? true,
    );

    const faqsData = useFaqsData(lang);
    const prefiltersData = usePrefiltersData(lang);

    const [selectedTab, setSelectedTab] = useState<string | null>(null);
    const [mobileView, setMobileView] = useState<string | null>(null);
    const [sectionsData, setSectionsData] = useState(null);
    const [onGoBackMobile, setOnGoBackMobile] = useState<(() => void) | null>(null);
    const [accessByAccessibilityDetails, setAccessByAccessibilityDetails] =
        useState<boolean>(false);

    const changeLang = (newLang: LangType) => {
        setLang(newLang);
        i18n.changeLanguage(newLang);
        localStorage.setItem(langStorageIndex, newLang);
        // Menu item labels (acesso_atividades.descricao) are language-dependent — refetch on language switch
        ParametrosAPI.buscarFuncionalidades()
            .then((response: any) => {
                dispatch(insertAtividades(response.data));
            })
            .catch((error: any) => APIHelper.tratarErro(error));
    };

    useEffect(() => {
        observer.current = new ResizeObserver(() => {
            setIsMobile(window.innerWidth <= 767);
        });
        observer.current.observe(document.body);
        return () => {
            observer.current?.disconnect();
        };
    }, []);

    const memoizedData = useMemo(
        () => ({
            lang,
            changeLang,
            serviceData,
            faqsData,
            prefiltersData,
            selectedTab,
            setSelectedTab,
            mobileView,
            setMobileView,
            sectionsData,
            setSectionsData,
            onGoBackMobile,
            setOnGoBackMobile,
            isMobile,
            accessByAccessibilityDetails,
            setAccessByAccessibilityDetails,
            siglaInstituicao,
            showDestaques,
        }),
        [
            lang,
            serviceData,
            faqsData,
            prefiltersData,
            selectedTab,
            mobileView,
            sectionsData,
            onGoBackMobile,
            isMobile,
            accessByAccessibilityDetails,
            siglaInstituicao,
            showDestaques,
        ],
    );

    return (
        <AppContext.Provider value={memoizedData}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): AppContextProps => {
    const context = useContext(AppContext);
    if (!context)
        throw new Error('useAppContext must be used within a AppContextProvider');
    return context;
};

export default AppContextProvider;